import { useCallback, useState } from 'react';

import { AddressAutocompleteFieldBlueprint, FieldBlueprint, FieldTypes } from '@zinnia/form-engine-sdk';

import {
  FieldDataFromBlueprint,
  FieldDataHookReturnValue,
  useCastBlueprintToFieldType,
} from '../../Helpers/helperTypes';

export type AddressAutocompleteFieldData = FieldDataFromBlueprint<
  AddressAutocompleteFieldBlueprint,
  'countryCode' | 'addressAutocompleteNodeId' | 'addressAutocompleteFields'
>;

const defaultProperties = {
  countryCode: 'CA',
  addressAutocompleteNodeId: '',
  addressAutocompleteFields: {
    streetAddress: '',
    city: '',
    stateOrProvince: '',
    postalCodeOrZip: '',
  },
} as const;

export type AddressAutocompleteFieldSetters = {
  setCountryCode: React.Dispatch<React.SetStateAction<AddressAutocompleteFieldData['countryCode']>>;
  setAddressAutocompleteNodeId: React.Dispatch<
    React.SetStateAction<AddressAutocompleteFieldData['addressAutocompleteNodeId']>
  >;
  setStreetAddress: React.Dispatch<
    React.SetStateAction<AddressAutocompleteFieldData['addressAutocompleteFields']['streetAddress']>
  >;
  setStateOrProvince: React.Dispatch<
    React.SetStateAction<AddressAutocompleteFieldData['addressAutocompleteFields']['stateOrProvince']>
  >;
  setPostalCodeOrZip: React.Dispatch<
    React.SetStateAction<AddressAutocompleteFieldData['addressAutocompleteFields']['postalCodeOrZip']>
  >;
  setCity: React.Dispatch<React.SetStateAction<AddressAutocompleteFieldData['addressAutocompleteFields']['city']>>;
};

export function useAddressAutocompleteFieldData({
  initialData,
}: {
  initialData?: Partial<FieldBlueprint>;
}): FieldDataHookReturnValue<AddressAutocompleteFieldData, AddressAutocompleteFieldSetters> {
  const initialDataAsAutocompleteField = useCastBlueprintToFieldType<AddressAutocompleteFieldBlueprint>(
    FieldTypes.autocomplete,
    initialData,
  );

  const [addressAutocompleteNodeId, setAddressAutocompleteNodeId] = useState<
    AddressAutocompleteFieldData['addressAutocompleteNodeId']
  >(initialDataAsAutocompleteField?.addressAutocompleteNodeId || defaultProperties.addressAutocompleteNodeId);

  const [countryCode, setCountryCode] = useState<AddressAutocompleteFieldData['countryCode']>(
    initialDataAsAutocompleteField?.countryCode || defaultProperties.countryCode,
  );

  const [city, setCity] = useState<AddressAutocompleteFieldData['addressAutocompleteFields']['city']>(
    initialDataAsAutocompleteField?.addressAutocompleteFields?.city || defaultProperties.addressAutocompleteFields.city,
  );

  const [stateOrProvince, setStateOrProvince] = useState<
    AddressAutocompleteFieldData['addressAutocompleteFields']['stateOrProvince']
  >(
    initialDataAsAutocompleteField?.addressAutocompleteFields?.stateOrProvince ||
      defaultProperties.addressAutocompleteFields.stateOrProvince,
  );

  const [streetAddress, setStreetAddress] = useState<
    AddressAutocompleteFieldData['addressAutocompleteFields']['streetAddress']
  >(
    initialDataAsAutocompleteField?.addressAutocompleteFields?.streetAddress ||
      defaultProperties.addressAutocompleteFields.streetAddress,
  );

  const [postalCodeOrZip, setPostalCodeOrZip] = useState<
    AddressAutocompleteFieldData['addressAutocompleteFields']['postalCodeOrZip']
  >(
    initialDataAsAutocompleteField?.addressAutocompleteFields?.postalCodeOrZip ||
      defaultProperties.addressAutocompleteFields.postalCodeOrZip,
  );

  const clearData = useCallback(() => {
    setAddressAutocompleteNodeId(
      initialDataAsAutocompleteField?.addressAutocompleteNodeId || defaultProperties.addressAutocompleteNodeId,
    );

    setCountryCode(initialDataAsAutocompleteField?.countryCode || defaultProperties.countryCode);

    setCity(
      initialDataAsAutocompleteField?.addressAutocompleteFields?.city ||
        defaultProperties.addressAutocompleteFields.city,
    );

    setStateOrProvince(
      initialDataAsAutocompleteField?.addressAutocompleteFields?.stateOrProvince ||
        defaultProperties.addressAutocompleteFields.stateOrProvince,
    );

    setStreetAddress(
      initialDataAsAutocompleteField?.addressAutocompleteFields?.streetAddress ||
        defaultProperties.addressAutocompleteFields.streetAddress,
    );

    setPostalCodeOrZip(
      initialDataAsAutocompleteField?.addressAutocompleteFields?.postalCodeOrZip ||
        defaultProperties.addressAutocompleteFields.postalCodeOrZip,
    );
  }, [setAddressAutocompleteNodeId, setCountryCode, setCity, setStateOrProvince, setStreetAddress, setPostalCodeOrZip]);

  return {
    data: {
      countryCode,
      addressAutocompleteNodeId,
      addressAutocompleteFields: {
        streetAddress,
        city,
        stateOrProvince,
        postalCodeOrZip,
      },
    },
    setters: {
      setCountryCode,
      setAddressAutocompleteNodeId,
      setCity,
      setStreetAddress,
      setPostalCodeOrZip,
      setStateOrProvince,
    },
    clearData,
  };
}
